import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faPlus,
  faNewspaper,
  faStar,
  faTags,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import {
  AdminPageBody,
  AdminLoadingState,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import Toast from "../components/Toast";
import DeleteNewsModal from "./components/news/DeleteNewsModal";
import NewsCategoriesModal from "./components/news/NewsCategoriesModal";
import NewsCard from "./components/news/NewsCard";
import { useAdminNews } from "../store/admin";

// ===== ADMIN NEWS MANAGEMENT — /api/admin/news =====
const STATUS_OPTIONS = ["DRAFT", "PUBLISHED"];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "published", label: "Published" },
  { value: "updated", label: "Updated" },
];

const NewsManagement = ({
  useNewsStore = useAdminNews,
  NavbarComponent = AdminNavBar,
  detailPath = "/admin/news",
} = {}) => {
  const navigate = useNavigate();
  // Every `/api/admin/news*` call lives in the admin store (useAdminNews).
  const {
    news,
    newsLoading: isLoading,
    newsTotal: total,
    newsPages: totalPages,
    newsCategories: categories,
    fetchNews,
    fetchNewsCategories,
    publishNews,
    unpublishNews,
    featureNews,
    unfeatureNews,
    deleteNewsById,
  } = useNewsStore();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [featuredFilter, setFeaturedFilter] = useState("");
  const [sort, setSort] = useState("newest");
  const [reload, setReload] = useState(false);

  const [deleteNews, setDeleteNews] = useState(null);
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);

  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);
  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  const refresh = () => setReload((r) => !r);

  // ── Fetch news categories for the create/edit dropdown ──
  useEffect(() => {
    fetchNewsCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced search ──
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // ── GET /api/admin/news?page&limit&search&status&featured&sort ──
  useEffect(() => {
    fetchNews({
      page: String(page),
      limit: "10",
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(featuredFilter ? { featured: featuredFilter } : {}),
      ...(sort ? { sort } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, statusFilter, featuredFilter, sort, reload]);

  // ── Publish / unpublish / feature (via the admin store) ──
  const handlePublish = async (item) => {
    const res = await publishNews(item.id);
    if (res.success) {
      showToast("News published");
      refresh();
    } else {
      showToast(res.error || "Action failed", false);
    }
  };

  const handleUnpublish = async (item) => {
    const res = await unpublishNews(item.id);
    if (res.success) {
      showToast("News unpublished");
      refresh();
    } else {
      showToast(res.error || "Action failed", false);
    }
  };

  const handleFeature = async (item) => {
    const res = item.featured ? await unfeatureNews(item.id) : await featureNews(item.id);
    if (res.success) {
      showToast(item.featured ? "Removed from featured" : "Marked as featured");
      refresh();
    } else {
      showToast(res.error || "Action failed", false);
    }
  };

  const openCreate = () => {
    navigate(`${detailPath}/create`);
  };

  const openEdit = (item) => {
    navigate(`${detailPath}/${item.id}/edit`);
  };

  const handleDelete = async (item) => {
    const res = await deleteNewsById(item.id);
    if (!res.success) return showToast(res.error || "Failed to delete news", false);
    showToast("News deleted");
    setDeleteNews(null);
    refresh();
  };

  const featuredNews = news.filter((n) => n.featured);
  const regularNews = news.filter((n) => !n.featured);

  return (
    <div>
      <NavbarComponent
        title="News Management"
        context="Create, publish and manage news articles across the platform."
      />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        {/* TOOLBAR */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="m-0 text-xl font-bold tracking-tight text-slate-900">News Center</h2>
            <p className="m-0 mt-1 text-sm text-slate-500">
              {total} article{total !== 1 ? "s" : ""} &bull; Manage publishing and featuring
            </p>
          </div>
                    <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsCategoriesModalOpen(true)}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <FontAwesomeIcon icon={faTags} /> Manage Categories
            </button>
            <button type="button" onClick={openCreate} className={adminPrimaryButton}>
              <FontAwesomeIcon icon={faPlus} /> Create News
            </button>
          </div>
        </div>

        {/* FILTERS */}
        <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex min-h-11 w-full max-w-sm items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 text-slate-500 focus-within:border-slate-400 focus-within:bg-white">
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
                placeholder="Search news by title or content..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                className="form-select"
                style={{ minWidth: "150px" }}
                value={statusFilter}
                onChange={(e) => {
                  setPage(1);
                  setStatusFilter(e.target.value);
                }}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>

              <select
                className="form-select"
                style={{ minWidth: "150px" }}
                value={featuredFilter}
                onChange={(e) => {
                  setPage(1);
                  setFeaturedFilter(e.target.value);
                }}
              >
                <option value="">All</option>
                <option value="true">Featured</option>
                <option value="false">Not Featured</option>
              </select>

              <select
                className="form-select"
                style={{ minWidth: "150px" }}
                value={sort}
                onChange={(e) => {
                  setPage(1);
                  setSort(e.target.value);
                }}
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    Sort: {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* CONTENT */}
        {isLoading ? (
          <div className="mt-6">
            <AdminLoadingState />
          </div>
        ) : news.length === 0 ? (
          <div className="mt-6 flex min-h-[280px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-6 text-center">
            <FontAwesomeIcon icon={faNewspaper} className="mb-3 text-4xl text-slate-300" />
            <h3 className="m-0 text-base font-semibold text-slate-900">No news found</h3>
            <p className="mt-2 mb-0 max-w-md text-sm text-slate-500">
              Adjust your filters or create your first news article.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-8">
                       {/* REGULAR NEWS */}
            <div>
              {featuredNews.length > 0 && (
                <div className="mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faNewspaper} className="text-slate-400" />
                  <h3 className="m-0 text-sm font-bold uppercase tracking-wide text-slate-700">
                    All News
                  </h3>
                </div>
              )}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {(regularNews.length > 0 ? regularNews : news).map((item) => (
                  <NewsCard
                    key={item.id}
                    item={item}
                    categories={categories}
                    onPublish={handlePublish}
                    onUnpublish={handleUnpublish}
                    onFeature={handleFeature}
                    onEdit={openEdit}
                    onDelete={setDeleteNews}
                    onView={(it) => navigate(`${detailPath}/${it.id}`)}
                  />
                ))}
              </div>
            </div>

            {/* FEATURED BAND */}
            {featuredNews.length > 0 && (
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <FontAwesomeIcon icon={faStar} className="text-amber-400" />
                  <h3 className="m-0 text-sm font-bold uppercase tracking-wide text-slate-700">
                    Featured News
                  </h3>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {featuredNews.map((item) => (
                    <NewsCard
                      key={item.id}
                      item={item}
                      categories={categories}
                      onPublish={handlePublish}
                      onUnpublish={handleUnpublish}
                      onFeature={handleFeature}
                      onEdit={openEdit}
                      onDelete={setDeleteNews}
                      onView={(it) => navigate(`${detailPath}/${it.id}`)}
                    />
                  ))}
                </div>
              </div>
            )}

 

            {/* PAGINATION */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                <span className="text-[13px] text-slate-500">
                  Page {page} of {totalPages}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page <= 1}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} size="sm" />
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    disabled={page >= totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={faChevronRight} size="sm" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </AdminPageBody>

      {/* Destructive and category management dialogs remain contextual. */}
      <DeleteNewsModal
        isOpen={!!deleteNews}
        news={deleteNews}
        onClose={() => setDeleteNews(null)}
        onConfirm={handleDelete}
      />
      <NewsCategoriesModal
        isOpen={isCategoriesModalOpen}
        onClose={() => setIsCategoriesModalOpen(false)}
        useNewsStore={useNewsStore}
        onSuccess={(msg) => {
          showToast(msg);
          fetchNewsCategories();
        }}
        onError={(msg, ok) => showToast(msg, ok)}
      />
    </div>
  );
};

export default NewsManagement;
